import { cloneDeep } from "lodash";

export function getMediaTypeFromUrl(url) {
  const extension = url.split('.').pop().split(/\#|\?/)[0].toLowerCase();

  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)) {
    return 'image';
  } else if (['mp4', 'mov', 'webm', 'mkv', 'avi'].includes(extension)) {
    return 'video';
  } else if (['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(extension)) {
    return 'audio';
  } else {
    return 'unknown';
  }
}

const knownRatios = {
  "1:1": 1 / 1,
  "16:9": 16 / 9,
  "9:16": 9 / 16,
  "4:6": 4 / 6,
  "6:4": 6 / 4,
  "6:1": 6 / 1,
  "1:6": 1 / 6,
  "5:4": 5 / 4,
  "4:5": 4 / 5,
  "4:1": 4 / 1,
  "1:4": 1 / 4,
  // "9:16": 9 / 16,
  // "21:9": 21 / 9,
  // "2:3": 2 / 3,
  // "3:2": 3 / 2,
  // "4:3": 4 / 3,
  // "3:4": 3 / 4,
};

/**
 * Check if an image or video (URL or File) matches a known ratio
 * @param {string|File} source - Media URL or File
 * @param {"image"|"video"} type - Media type
 * @param {number} [tolerance=0.02] - Allowed floating point tolerance (2%)
 * @returns {Promise<{matched: boolean, ratio?: string, actual: number}>}
 */
export async function checkMediaRatio(source, type = undefined, tolerance = 0.02) {
  const isFile = typeof File !== "undefined" && source instanceof File;
  const isBlob = typeof Blob !== "undefined" && source instanceof Blob;

  // try to infer type if not provided
  if (!type) {
    if (isFile) {
      const mime = source.type || "";
      if (mime.startsWith("image/")) type = "image";
      else if (mime.startsWith("video/")) type = "video";
    } else if (typeof source === "string") {
      const pathWithoutQuery = source.split("?")[0] || source;
      const ext = (pathWithoutQuery.split(".").pop() || "").toLowerCase();
      const imageExts = ["png","jpg","jpeg","gif","webp","bmp","svg","tif","tiff"];
      const videoExts = ["mp4","webm","ogg","mov","mkv","m4v"];
      if (imageExts.includes(ext)) type = "image";
      else if (videoExts.includes(ext)) type = "video";
    }
  }

  if (!type) {
    throw new Error("Unable to determine media type. Pass 'image' or 'video' as second arg.");
  }

  return new Promise((resolve, reject) => {
    let objectUrl = null;
    let cleanedUp = false;

    function cleanup() {
      if (cleanedUp) return;
      cleanedUp = true;
      if (objectUrl) {
        try { URL.revokeObjectURL(objectUrl); } catch (e) { /* ignore */ }
        objectUrl = null;
      }
    }

    function finalize(width, height) {
      cleanup();
      if (!width || !height) {
        reject(new Error("Could not determine dimensions"));
        return;
      }
      const actual = width / height;

      // find exact match within tolerance
      for (const [key, value] of Object.entries(knownRatios)) {
        if (Math.abs(actual - value) <= tolerance) {
          resolve({ matched: true, ratio: key, actual });
          return;
        }
      }

      // find closest match (minimum absolute difference)
      let closestRatio = null;
      let minDiff = Infinity;
      for (const [key, value] of Object.entries(knownRatios)) {
        const diff = Math.abs(actual - value);
        if (diff < minDiff) {
          minDiff = diff;
          closestRatio = key;
        }
      }

      resolve({ matched: false, actual, closest: { ratio: closestRatio, diff: minDiff } });
    }

    // Helper to set src (File/Blob -> objectURL, or string directly)
    function setSrc(el) {
      try {
        if (isFile || isBlob) {
          objectUrl = URL.createObjectURL(source);
          el.src = objectUrl;
        } else {
          // for cross-origin images, try without crossOrigin first. If you need canvas access set crossOrigin='anonymous'
          if (type === "image") el.crossOrigin = "anonymous";
          el.src = source;
        }
      } catch (err) {
        cleanup();
        reject(err);
      }
    }

    if (type === "image") {
      const img = new Image();

      // set crossOrigin to anonymous so some CDNs/S3 allow it (if CORS configured)
      img.crossOrigin = "anonymous";

      img.onload = () => {
        // naturalWidth/naturalHeight are the actual image pixel dimensions
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        finalize(w, h);
      };
      img.onerror = (ev) => {
        cleanup();
        reject(new Error("Failed to load image"));
      };

      setSrc(img);
      return;
    }

    if (type === "video") {
      const vid = document.createElement("video");
      vid.preload = "metadata";
      vid.muted = true; // not necessary but safe
      vid.playsInline = true;

      const onMeta = () => {
        // videoWidth / videoHeight available after loadedmetadata
        const w = vid.videoWidth;
        const h = vid.videoHeight;
        finalize(w, h);
      };

      const onErr = () => {
        cleanup();
        reject(new Error("Failed to load video metadata"));
      };

      vid.addEventListener("loadedmetadata", onMeta, { once: true });
      vid.addEventListener("error", onErr, { once: true });

      setSrc(vid);
      return;
    }

    reject(new Error("Unsupported type"));
  });
}

export async function checkMediaRatio2(source, type = undefined, tolerance = 0.02,ratio) {
  const isFile = typeof File !== "undefined" && source instanceof File;
  const isBlob = typeof Blob !== "undefined" && source instanceof Blob;

  // try to infer type if not provided
  if (!type) {
    if (isFile) {
      const mime = source.type || "";
      if (mime.startsWith("image/")) type = "image";
      else if (mime.startsWith("video/")) type = "video";
    } else if (typeof source === "string") {
      const pathWithoutQuery = source.split("?")[0] || source;
      const ext = (pathWithoutQuery.split(".").pop() || "").toLowerCase();
      const imageExts = ["png","jpg","jpeg","gif","webp","bmp","svg","tif","tiff"];
      const videoExts = ["mp4","webm","ogg","mov","mkv","m4v"];
      if (imageExts.includes(ext)) type = "image";
      else if (videoExts.includes(ext)) type = "video";
    }
  }

  if (!type) {
    throw new Error("Unable to determine media type. Pass 'image' or 'video' as second arg.");
  }

  return new Promise((resolve, reject) => {
    let objectUrl = null;
    let cleanedUp = false;

    function cleanup() {
      if (cleanedUp) return;
      cleanedUp = true;
      if (objectUrl) {
        try { URL.revokeObjectURL(objectUrl); } catch (e) { /* ignore */ }
        objectUrl = null;
      }
    }

    function finalize(width, height) {
      cleanup();
      if (!width || !height) {
        reject(new Error("Could not determine dimensions"));
        return;
      }
      const actual = width / height;

      // find exact match within tolerance
      for (const [key, value] of Object.entries(ratio)) {
        if (Math.abs(actual - value) <= tolerance) {
          resolve({ matched: true, ratio: key, actual });
          return;
        }
      }

      // find closest match (minimum absolute difference)
      let closestRatio = null;
      let minDiff = Infinity;
      for (const [key, value] of Object.entries(ratio)) {
        const diff = Math.abs(actual - value);
        if (diff < minDiff) {
          minDiff = diff;
          closestRatio = key;
        }
      }

      resolve({ matched: false, actual, closest: { ratio: closestRatio, diff: minDiff } });
    }

    // Helper to set src (File/Blob -> objectURL, or string directly)
    function setSrc(el) {
      try {
        if (isFile || isBlob) {
          objectUrl = URL.createObjectURL(source);
          el.src = objectUrl;
        } else {
          // for cross-origin images, try without crossOrigin first. If you need canvas access set crossOrigin='anonymous'
          if (type === "image") el.crossOrigin = "anonymous";
          el.src = source;
        }
      } catch (err) {
        cleanup();
        reject(err);
      }
    }

    if (type === "image") {
      const img = new Image();

      // set crossOrigin to anonymous so some CDNs/S3 allow it (if CORS configured)
      img.crossOrigin = "anonymous";

      img.onload = () => {
        // naturalWidth/naturalHeight are the actual image pixel dimensions
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        finalize(w, h);
      };
      img.onerror = (ev) => {
        cleanup();
        reject(new Error("Failed to load image"));
      };

      setSrc(img);
      return;
    }

    if (type === "video") {
      const vid = document.createElement("video");
      vid.preload = "metadata";
      vid.muted = true; // not necessary but safe
      vid.playsInline = true;

      const onMeta = () => {
        // videoWidth / videoHeight available after loadedmetadata
        const w = vid.videoWidth;
        const h = vid.videoHeight;
        finalize(w, h);
      };

      const onErr = () => {
        cleanup();
        reject(new Error("Failed to load video metadata"));
      };

      vid.addEventListener("loadedmetadata", onMeta, { once: true });
      vid.addEventListener("error", onErr, { once: true });

      setSrc(vid);
      return;
    }

    reject(new Error("Unsupported type"));
  });
}

export const checkAspectRatio = (url, expectedRatio, mediaType) => {
  return new Promise((resolve) => {
    const [expW, expH] = expectedRatio.split(':').map(Number);
    const expected = expW / expH;

    if (mediaType === 'image') {
      const img = new Image();
      img.onload = () => {
        const actual = img.width / img.height;
        resolve(Math.abs(actual - expected) < 0.05); // allow small margin
      };
      img.onerror = () => resolve(false);
      img.src = url;
    } else if (mediaType === 'video') {
      const video = document.createElement('video');
      video.onloadedmetadata = () => {
        const actual = video.videoWidth / video.videoHeight;
        resolve(Math.abs(actual - expected) < 0.05);
      };
      video.onerror = () => resolve(false);
      video.src = url;
    } else {
      resolve(true);
    }
  });
};

export const getMediaDimensionsAndRatio = (file) => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);

    if (file.type.startsWith('image/')) {
      const img = new Image();
      img.onload = () => {
        const ratio = getClosestStandardRatio(img.width, img.height);
        //const ratio = getSimplifiedAspectRatio(img.width,img.height);
        resolve({ width: img.width, height: img.height, mediaType: 'image', ratio });
      };
      img.onerror = reject;
      img.src = url;
    } else if (file.type.startsWith('video/')) {
      const video = document.createElement('video');
      video.onloadedmetadata = () => {
        const ratio = getClosestStandardRatio(video.videoWidth, video.videoHeight);
        //const ratio = getSimplifiedAspectRatio(video.videoWidth,video.videoHeight);
        resolve({ width: video.videoWidth, height: video.videoHeight, mediaType: 'video', ratio });
      };
      video.onerror = reject;
      video.src = url;
    } else {
      reject(new Error('Unsupported media type'));
    }
  });
};

export const getSimplifiedAspectRatio = (width, height, precision = 1) => {
  if (!width || !height) return null;

  // Make height as 1, scale width accordingly
  const ratio = width / height;
  const formatted = (ratio >= 1)
    ? `1 : ${(ratio).toFixed(precision)}`
    : `${(1 / ratio).toFixed(precision)} : 1`;

  return formatted;
};

const getClosestStandardRatio = (width, height, precision = 2) => {
  const actualRatio = width / height;
  let closestRatio = "";
  let minDiff = Infinity;

  for (const [label, value] of Object.entries(knownRatios)) {
    const diff = Math.abs(actualRatio - value);
    if (diff < minDiff) {
      minDiff = diff;
      closestRatio = label;
    }
  }

  return closestRatio;
};

export const getValueByPath = (obj, path) => {
  return path.split('.').reduce((acc, key) => {
    if (acc && typeof acc === 'object') {
      return acc[key];
    }
    return undefined;
  }, obj);
};

export const removeKeyFromObjectByPath = (obj, path) => {
  const keys = path.split('.');
  const updated = { ...obj };
  let temp = updated;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    // If path doesn't exist, return the original object
    if (typeof temp[key] !== 'object' || temp[key] === null) {
      return obj;
    }
    temp[key] = Array.isArray(temp[key]) ? [...temp[key]] : { ...temp[key] };
    temp = temp[key];
  }

  const lastKey = keys[keys.length - 1];

  if (Array.isArray(temp) && /^\d+$/.test(lastKey)) {
    temp.splice(parseInt(lastKey), 1);
  } else {
    delete temp[lastKey];
  }

  return updated;
};

export const updateStateObjectData = (state, setState, path, value) => {
  const keys = path.split(".");
  setState(prev => {
    const updated = { ...prev };
    let obj = updated;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];

      // If key doesn't exist OR is not an object/array, create an empty object
      if (typeof obj[key] !== 'object' || obj[key] === null) {
        // If the next key is a number, treat it as an array
        const nextKey = keys[i + 1];
        obj[key] = /^\d+$/.test(nextKey) ? [] : {};
      }

      obj = obj[key];
    }

    const lastKey = keys[keys.length - 1];

    // If updating an array index (like winPositions.0.position)
    if (Array.isArray(obj) && /^\d+$/.test(lastKey)) {
      obj[parseInt(lastKey)] = value;
    } else {
      obj[lastKey] = value;
    }

    return updated;
  });
  console.log("Updated setState => ", state);
}

export function getYearRange(from = 1900) {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = from; year <= currentYear; year++) {
    years.push(year);
  }
  return years;
}

export const capitalizeFirst = str => str?.charAt(0)?.toUpperCase() + str?.slice(1);